import { useEffect, useState } from "react";
import axios from "axios";
import {
    inDateRange,
    normalizeBotError,
    normalizeBotErrorSummary,
    normalizeCronLog,
    normalizeCronSummary,
} from "./normalizeLogEntry";

// BotErrorLog only ever has WARNING/ERROR severities (see BotErrorLogService) -
// there's no INFO row on that side, and no type other than Telegram sends.
function shouldFetchCronLogs(type) {
    return !type || type !== "TELEGRAM_SEND";
}
function shouldFetchBotErrors(type, level) {
    if (type && type !== "TELEGRAM_SEND") return false;
    if (level === "INFO") return false;
    return true;
}

function buildQuery(params) {
    const parts = Object.entries(params)
        .filter(([, v]) => v !== "" && v != null)
        .map(([k, v]) => encodeURIComponent(k) + "=" + encodeURIComponent(v));
    return parts.length ? "?" + parts.join("&") : "";
}

export default function useCronLogFeed({ type, level, dateFrom, dateTo, viewMode }) {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        let cancelled = false;
        setStatus("loading");

        const cronType = type && type !== "TELEGRAM_SEND" ? type : "";
        const requests = [];

        if (shouldFetchCronLogs(type)) {
            const path = viewMode === "recent" ? "/api/cron-logs" : "/api/cron-logs/summary";
            const query = buildQuery(
                viewMode === "recent"
                    ? { type: cronType, level, from: dateFrom, to: dateTo, limit: 200 }
                    : { type: cronType, level, from: dateFrom, to: dateTo }
            );
            requests.push(
                axios
                    .get(path + query, { withCredentials: true })
                    .then((res) => (Array.isArray(res.data) ? res.data : []))
                    .catch(() => [])
            );
        } else {
            requests.push(Promise.resolve([]));
        }

        if (shouldFetchBotErrors(type, level)) {
            const path = viewMode === "recent" ? "/api/bot-errors" : "/api/bot-errors/summary";
            const query = buildQuery({ severity: level || "" });
            requests.push(
                axios
                    .get(path + query, { withCredentials: true })
                    .then((res) => (Array.isArray(res.data) ? res.data : []))
                    .catch(() => [])
            );
        } else {
            requests.push(Promise.resolve([]));
        }

        Promise.all(requests)
            .then(([cronRows, botRows]) => {
                if (cancelled) return;

                const normalizedCron =
                    viewMode === "recent" ? cronRows.map(normalizeCronLog) : cronRows.map(normalizeCronSummary);

                const normalizedBot = (
                    viewMode === "recent" ? botRows.map(normalizeBotError) : botRows.map(normalizeBotErrorSummary)
                ).filter((row) => inDateRange(row, dateFrom, dateTo));

                const merged = [...normalizedCron, ...normalizedBot].sort((a, b) => {
                    const ta = (a.lastOccurredAt || a.timestamp)?.toMillis() || 0;
                    const tb = (b.lastOccurredAt || b.timestamp)?.toMillis() || 0;
                    return tb - ta;
                });

                setRows(merged);
                setStatus("done");
            })
            .catch(() => {
                if (!cancelled) setStatus("error");
            });

        return () => {
            cancelled = true;
        };
    }, [type, level, dateFrom, dateTo, viewMode]);

    return { rows, status };
}
